import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Music2 } from 'lucide-react';
import PageTransition from './PageTransition';
import CustomCursor from './CustomCursor';

const albums = [
  { id: 1, title: "depth training", url: "https://jamblu.bandcamp.com/album/depth-training", year: 2024, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/46xrgdhf_Depth%20Training.jpg" },
  { id: 2, title: "bone ringing", url: "https://jamblu.bandcamp.com/album/bone-ringing", year: 2023, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/xzu2cwrj_Boneringing%20mountain.png" },
  { id: 3, title: "t.d.f.d.t.u", url: "https://jamblu.bandcamp.com/album/t-d-f-d-t-u", year: 2022, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/avz8i5et_TDFDTU.png" },
  { id: 4, title: "gone swimming", url: "https://jamblu.bandcamp.com/album/gone-swimming", year: 2021, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/g1inliel_Gone%20Swimming.jpg" },
  { id: 5, title: "blue valley golden mountain", url: "https://jamblu.bandcamp.com/track/blue-valley-golden-mountain", year: 2020, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/741kaf3p_Blue%20Valley%20Golden%20Mountain.jpg" },
  { id: 6, title: "music to be nothing to", url: "https://jamblu.bandcamp.com/album/music-to-be-nothing-to", year: 2019, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/90rwim6f_Music%20To%20Be%20Nothing%20To.jpg" },
  { id: 7, title: "service animal", url: "https://jamblu.bandcamp.com/album/service-animal", year: 2018, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/04bfsdhw_Service%20Animal.jpg" },
  { id: 8, title: "how deep is your database", url: "https://jamblu.bandcamp.com/album/how-deep-is-your-database", year: 2017, cover: "https://customer-assets.emergentagent.com/job_913ef00e-beda-4a41-bb6b-322887eeb770/artifacts/rxo08ysf_How%20Deep%20Is%20Your%20Database.jpg" }
];

const MusicPage = () => {
  const navigate = useNavigate();
  const [hoveredAlbum, setHoveredAlbum] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 10
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeAlbum) setActiveAlbum(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeAlbum]);

  return (
    <PageTransition>
      <CustomCursor />
      <div className="music-page-v2">
        {/* Stars background */}
        <div className="music-stars">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="star-dot"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        {/* Header */}
        <header className="music-v2-header">
          <button className="music-v2-close" onClick={() => navigate('/')}>
            <X size={24} />
          </button>
          <h1>music</h1>
          <div style={{ width: 50 }} />
        </header>

        {/* 3D Album Grid */}
        <div 
          className="music-v2-grid"
          style={{
            '--rotate-x': `${mousePos.y}deg`,
            '--rotate-y': `${mousePos.x}deg`
          }}
        >
          {albums.map((album, index) => (
            <div
              key={album.id}
              className={`music-v2-album ${hoveredAlbum === album.id ? 'hovered' : ''}`}
              style={{ 
                animationDelay: `${index * 0.1}s`,
                '--hue': (index * 40) % 360
              }}
              onMouseEnter={() => setHoveredAlbum(album.id)}
              onMouseLeave={() => setHoveredAlbum(null)}
              onClick={() => setActiveAlbum(album)}
            >
              <div className="album-v2-cover">
                {album.cover ? (
                  <img src={album.cover} alt={album.title} className="album-cover-img" />
                ) : (
                  <span>{album.title.charAt(0)}</span>
                )}
                <div className="album-v2-vinyl" />
              </div>
              <div className="album-v2-label">
                <h3>{album.title}</h3>
                <span>{album.year}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {activeAlbum && (
          <div className="music-v2-modal" onClick={() => setActiveAlbum(null)}>
            <div className="music-v2-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="music-v2-modal-close" onClick={() => setActiveAlbum(null)}>
                <X size={20} />
              </button>
              <div className="music-v2-modal-cover">
                {activeAlbum.cover ? (
                  <img src={activeAlbum.cover} alt={activeAlbum.title} />
                ) : (
                  <span>{activeAlbum.title.charAt(0)}</span>
                )}
              </div>
              <h2>{activeAlbum.title}</h2>
              <span className="music-v2-modal-year">{activeAlbum.year}</span>
              <a 
                href={activeAlbum.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="music-v2-bandcamp-btn"
              >
                <Music2 size={18} />
                listen on bandcamp
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default MusicPage;
